<template>
    <UApp>
        <template v-if="isMounted">
            <!-- VitePwaManifest component - required for PWA manifest -->
            <VitePwaManifest />

            <!-- Auto-update notification (brief notification only) -->
            <div
                v-show="showUpdateNotification"
                class="fixed top-4 right-4 z-50 max-w-sm rounded-lg bg-green-600 p-4 text-white shadow-lg"
            >
                <div class="flex items-center space-x-2">
                    <svg
                        class="h-5 w-5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                    >
                        <path
                            fill-rule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clip-rule="evenodd"
                        />
                    </svg>
                    <span>App wird aktualisiert…</span>
                </div>
            </div>

            <!-- PWA Install Prompt -->
            <div
                v-show="$pwa?.showInstallPrompt && !$pwa?.isPWAInstalled"
                class="fixed right-4 bottom-4 max-w-sm rounded-lg border bg-white p-4 shadow-lg"
            >
                <div class="flex items-start space-x-3">
                    <div class="flex-shrink-0">
                        <svg
                            class="h-6 w-6 text-blue-600"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                        >
                            <path
                                d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"
                            />
                        </svg>
                    </div>
                    <div class="flex-1">
                        <h3 class="text-sm font-medium text-gray-900">
                            App installieren
                        </h3>
                        <p class="text-sm text-gray-500">
                            Füge diese App für schnellen Zugriff zu deinem
                            Startbildschirm hinzu.
                        </p>
                        <div class="mt-2 flex space-x-2">
                            <button
                                class="rounded bg-blue-600 px-3 py-1 text-xs text-white hover:bg-blue-700"
                                @click="$pwa?.install()"
                            >
                                Installieren
                            </button>
                            <button
                                class="text-xs text-gray-500 hover:text-gray-700"
                                @click="$pwa?.cancelInstall()"
                            >
                                Nicht jetzt
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <NuxtLayout />

            <!-- Essentials Error Overlay -->
            <div
                v-if="store.essentialsError"
                class="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-black/50"
            >
                <div class="rounded-lg bg-white p-8 text-center shadow-2xl">
                    <h2 class="text-xl font-bold text-gray-800">
                        Verbindungsproblem
                    </h2>
                    <p class="mt-2 text-gray-600">
                        Wichtige Anwendungsdaten konnten nicht geladen werden.
                        <br />
                        Bitte überprüfen Sie Ihre Internetverbindung.
                    </p>
                    <button
                        class="mt-6 rounded-md bg-green-600 px-5 py-2 font-semibold text-white transition hover:bg-green-700"
                        @click="store.fetchEssentials()"
                    >
                        Erneut versuchen
                    </button>
                </div>
            </div>
        </template>
    </UApp>
</template>

<script setup lang="ts">
import { useDefaultStore } from "~/stores/default";

const store = useDefaultStore();
const isMounted = ref(false);
const showUpdateNotification = ref(false);

// Access the PWA plugin
const { $pwa, $tracker: tracker } = useNuxtApp();

if (tracker) {
    tracker.start();

    watch(
        () => store.auth.username,
        () => {
            tracker.setUserID(store.auth.username);
        },
    );
}

const pageTitle = useState("pageTitle");
useHead({
    title: computed(() => `${pageTitle.value}`),
});

onMounted(() => {
    store.checkAuth();
    isMounted.value = true;

    // Initialize PWA watchers
    initializePWAWatchers();

    // Debug PWA status (remove in production)
    if (import.meta.dev && $pwa) {
        console.log("PWA Status:", {
            needRefresh: $pwa.needRefresh,
            offlineReady: $pwa.offlineReady,
            showInstallPrompt: $pwa.showInstallPrompt,
            isPWAInstalled: $pwa.isPWAInstalled,
            swActivated: $pwa.swActivated,
            registrationError: $pwa.registrationError,
        });
    }
});

// Reload-on-update — three independent signals route through a single
// guarded scheduler so the page reloads no matter which path fires first.
//
//  Signal 1: $pwa.needRefresh        — vite-pwa-nuxt's high-level update event
//  Signal 2: controllerchange        — new SW just claimed the page
//  Signal 3: visibilitychange        — wake-up; force a SW update poll
//
// Why all three: with workbox.cleanupOutdatedCaches: true, the moment the
// new SW activates it wipes the old precache. The running tab has stale JS
// in memory — every subsequent lazy-import is a 404 waiting to happen. We
// MUST reload, and we can't trust any single signal to fire reliably across
// device states (background-throttled mobile Chrome is the worst offender).

function initializePWAWatchers() {
    if (!$pwa) {
        console.log("PWA plugin not available");
        return;
    }

    // Single-flight scheduler — first call wins, later calls are no-ops.
    let reloadTimer: ReturnType<typeof setTimeout> | null = null;
    const scheduleReload = (reason: string, delayMs = 3000) => {
        if (reloadTimer !== null) {
            console.log(`[pwa] reload already scheduled, ignoring: ${reason}`);
            return;
        }
        console.log(`[pwa] scheduling reload in ${delayMs}ms — ${reason}`);
        showUpdateNotification.value = true;
        reloadTimer = setTimeout(() => {
            // We use location.reload() rather than $pwa.updateServiceWorker:
            // in autoUpdate mode the SW has already skip-waited and claimed
            // by the time any signal here fires, so there's nothing left to
            // "update" — we just throw away the old JS in memory.
            //
            // Defensive try/catch: location.reload() is not specced to
            // throw, but exotic embeds (kiosk WebViews etc.) have been
            // known to. Retry once after 1 s if so.
            try {
                window.location.reload();
            } catch (err) {
                console.error("[pwa] reload threw, retrying in 1s", err);
                setTimeout(() => window.location.reload(), 1000);
            }
        }, delayMs);
    };

    // Signal 1 — vite-pwa-nuxt's high-level update flag.
    watch(
        () => $pwa.needRefresh,
        (needRefresh) => {
            if (needRefresh) scheduleReload("$pwa.needRefresh");
        },
    );

    // Signal 2 — the new SW has taken control of the page. After this
    // moment, the precache for the OLD build is gone (cleanupOutdatedCaches)
    // and any chunk the tab still references in memory will 404 from the
    // network. Reload immediately, with the same 3 s toast courtesy.
    //
    // Skip first-install: on a brand-new device with no prior controller,
    // the just-registered SW becomes the controller and fires this event
    // once. There's no old JS to reload away from in that case.
    if (typeof navigator !== "undefined" && navigator.serviceWorker) {
        const hadControllerAtStart = !!navigator.serviceWorker.controller;
        navigator.serviceWorker.addEventListener("controllerchange", () => {
            if (!hadControllerAtStart) return;
            scheduleReload("serviceWorker.controllerchange");
        });
    }

    // Signal 3 — when a backgrounded/sleeping tab wakes up, force the SW
    // update check immediately rather than waiting up to 60s for the
    // periodicSyncForUpdates timer (which mobile browsers throttle hard).
    // This isn't a reload trigger directly — it's a "kick the SW into
    // checking now" so signals 1 and 2 fire faster.
    document.addEventListener("visibilitychange", () => {
        if (document.visibilityState !== "visible") return;
        if (typeof navigator === "undefined" || !navigator.serviceWorker) return;
        navigator.serviceWorker
            .getRegistration()
            .then((reg) => reg?.update())
            .catch((err) => {
                // Network errors here are fine — the next periodic check
                // will retry. Just don't let it become an unhandled rejection.
                console.warn("[pwa] forced SW update check failed", err);
            });
    });

    // Diagnostics-only watchers (kept from previous version).
    watch(
        () => $pwa.swActivated,
        (activated) => {
            if (activated) console.log("Service Worker activated");
        },
    );
    watch(
        () => $pwa.registrationError,
        (error) => {
            if (error) {
                console.error("Service Worker registration error");
                showToast(
                    "Service Worker konnte nicht registriert werden",
                    "error",
                );
            }
        },
    );
    watch(
        () => $pwa.isPWAInstalled,
        (installed) => {
            if (installed) {
                console.log("PWA is installed");
                showToast("App erfolgreich installiert!", "success");
            }
        },
    );
}

// Utility function for showing toast notifications
function showToast(
    message: string,
    type: "info" | "success" | "error" = "info",
) {
    // If using Nuxt UI or another toast system, implement here
    // For now, using console and you can replace with your preferred toast
    console.log(`[${type.toUpperCase()}] ${message}`);

    // Example with Nuxt UI (if available):
    // const toast = useToast();
    // toast.add({ title: message, color: type === 'error' ? 'red' : type === 'success' ? 'green' : 'blue' });
}
</script>

<style>
@import "tailwindcss";
@import "@nuxt/ui";

/* PWA transition animations */
.pwa-slide-enter-active,
.pwa-slide-leave-active {
    transition: all 0.3s ease;
}

.pwa-slide-enter-from {
    transform: translateY(-100%);
    opacity: 0;
}

.pwa-slide-leave-to {
    transform: translateY(-100%);
    opacity: 0;
}
</style>
